import React from "react";
import { Box, Modal, Tabs, TabList, Tab, TabPanel, List, ListItem } from "@mui/joy";
import { CloseRoundedIcon, CheckCircleIcon, CancelIcon } from "../../styles/StyledMUI";
import type { TheoryTest, TheoryTestTemplate } from "../../api/theoryTests/TheoryTest.typed";
import IconButtonCTA from "../common/IconButtonCTA";
import DarkCard from "../common/DarkCard";
import DarkTypography from "../common/DarkTypography";
import { colorTokens, textTokens, withAlpha } from "../../styles/globalStyles";

interface TheoryTestHistoryModalProps {
  tests: TheoryTest[];
  template?: TheoryTestTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

function resultFraction(result: number): number {
  return result <= 1 ? result : result / 100;
}

function compareAnswers(providedAnswers: string[], correctAnswers: string[]): boolean {
  return (
    providedAnswers.length === correctAnswers.length &&
    providedAnswers.every((a) => correctAnswers.includes(a))
  );
}

export default function TheoryTestHistoryModal({
  tests,
  template,
  isOpen,
  onClose,
}: TheoryTestHistoryModalProps) {
  const [selectedTab, setSelectedTab] = React.useState(0);

  React.useEffect(() => {
    setSelectedTab((t) => Math.min(t, Math.max(0, tests.length - 1)));
  }, [tests.length]);

  if (!tests || tests.length === 0) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      sx={{
        zIndex: 196000,
        "& .MuiModal-backdrop": {
          zIndex: -1,
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
          backgroundColor: withAlpha(colorTokens.bg.overlay, 0.55),
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100%",
          p: { xs: 1, sm: 2 },
          boxSizing: "border-box",
        }}
      >
        <DarkCard
          sx={{
            width: "100%",
            maxWidth: { xs: "min(95vw, 720px)", sm: "min(95vw, 900px)" },
            maxHeight: "min(92dvh, 900px)",
            height: "min(92dvh, 900px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 0,
            boxShadow: `0 8px 32px ${withAlpha(colorTokens.bg.overlay, 0.45)}`,
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 2.5 },
              pb: 1.5,
              borderBottom: `1px solid ${colorTokens.border.default}`,
              bgcolor: colorTokens.bg.surface,
            }}
          >
            <DarkTypography component="h2" level="title-md" colorVariant="title">
              Historique des résultats
            </DarkTypography>
            <IconButtonCTA
              aria-label="Fermer l'historique des résultats"
              ctaVariant="neutral"
              size="sm"
              onClick={onClose}
            >
              <CloseRoundedIcon />
            </IconButtonCTA>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: colorTokens.bg.surface,
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value as number)}
              sx={{
                bgcolor: colorTokens.bg.surface,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <TabList
                sx={{
                  pt: { xs: 1, sm: 1.5 },
                  px: { xs: 1, sm: 2 },
                  borderBottom: `1px solid ${colorTokens.border.default}`,
                  gap: 1,
                  overflowX: "auto",
                  flexShrink: 0,
                  bgcolor: colorTokens.bg.surface,
                  "::-webkit-scrollbar": { height: "8px" },
                  "::-webkit-scrollbar-track": { bgcolor: colorTokens.bg.surface },
                  "::-webkit-scrollbar-thumb": {
                    bgcolor: colorTokens.border.strong,
                    borderRadius: "4px",
                  },
                  "& .MuiTab-root": {
                    color: withAlpha(textTokens.color.primary, 0.65),
                    minWidth: "auto",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    "&.Mui-selected": {
                      color: textTokens.color.inverse,
                      fontWeight: 600,
                      bgcolor: colorTokens.bg.sheetLight,
                      borderRadius: "sm",
                    },
                  },
                }}
              >
                {tests.map((test, index) => (
                  <Tab key={index}>
                    #{test.Attempt} —{" "}
                    {resultFraction(test.Result).toLocaleString("fr-FR", {
                      style: "percent",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </Tab>
                ))}
              </TabList>
              {tests.map((test, index) => (
                <TabPanel
                  key={index}
                  value={index}
                  sx={{
                    p: { xs: 1, sm: 2 },
                    overflow: "auto",
                    flex: 1,
                    minHeight: 0,
                    bgcolor: colorTokens.bg.surface,
                    color: textTokens.color.primary,
                  }}
                >
                  <DarkTypography level="body-sm" colorVariant="secondary" sx={{ mb: 2 }}>
                    Date : {new Date(test.LastAttemptDate).toLocaleDateString("fr-FR")}
                  </DarkTypography>

                  <List sx={{ "--List-gap": "8px" }}>
                    {test.Form.map((answer, qIndex) => {
                      const templateQuestion =
                        template?.TheoryTest.find((q) => q.ID === answer.ID) ??
                        template?.TheoryTest[qIndex];
                      const correctAnswers = templateQuestion?.CorrectAnswers;
                      const hasCorrectAnswers = Boolean(
                        correctAnswers && correctAnswers.length > 0,
                      );
                      const isCorrect =
                        hasCorrectAnswers && correctAnswers
                          ? compareAnswers(answer.ProvidedAnswers, correctAnswers)
                          : Boolean(answer.Validated);

                      return (
                        <ListItem
                          key={answer.ID ?? qIndex}
                          sx={{
                            bgcolor: colorTokens.bg.surfaceElevated,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: isCorrect
                              ? colorTokens.status.success
                              : colorTokens.status.danger,
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-start",
                            gap: { xs: 1.5, sm: 2 },
                            p: { xs: 1, sm: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 40,
                              flexShrink: 0,
                              pt: 0.25,
                            }}
                            aria-label={isCorrect ? "Réponse correcte" : "Réponse incorrecte"}
                          >
                            {isCorrect ? (
                              <CheckCircleIcon
                                sx={{ fontSize: "2rem", color: colorTokens.status.success }}
                                aria-hidden
                              />
                            ) : (
                              <CancelIcon
                                sx={{ fontSize: "2rem", color: colorTokens.status.danger }}
                                aria-hidden
                              />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <DarkTypography
                              level="title-sm"
                              colorVariant="primary"
                              sx={{ mb: 1, fontStyle: "italic" }}
                            >
                              {answer.QuestionText}
                            </DarkTypography>
                            <DarkTypography
                              level="body-sm"
                              colorVariant="primary"
                              sx={{ mb: 0.5, color: withAlpha(textTokens.color.primary, 0.85) }}
                            >
                              Réponses données :
                            </DarkTypography>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                              {answer.ProvidedAnswers.map((ans, aIndex) => {
                                const inCorrectSet =
                                  hasCorrectAnswers && correctAnswers
                                    ? correctAnswers.includes(ans)
                                    : false;
                                return (
                                  <DarkTypography
                                    key={aIndex}
                                    level="body-sm"
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: "sm",
                                      color: textTokens.color.primary,
                                      bgcolor: hasCorrectAnswers
                                        ? inCorrectSet
                                          ? colorTokens.status.success
                                          : colorTokens.status.danger
                                        : colorTokens.bg.surfaceMuted,
                                    }}
                                  >
                                    {ans}
                                  </DarkTypography>
                                );
                              })}
                            </Box>
                            {templateQuestion &&
                              !isCorrect &&
                              templateQuestion.CorrectAnswers &&
                              templateQuestion.CorrectAnswers.length > 0 && (
                                <>
                                  <DarkTypography
                                    level="body-sm"
                                    colorVariant="primary"
                                    sx={{
                                      mb: 0.5,
                                      color: withAlpha(textTokens.color.primary, 0.85),
                                    }}
                                  >
                                    Réponses correctes :
                                  </DarkTypography>
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                    {templateQuestion.CorrectAnswers.map((ans, aIndex) => (
                                      <DarkTypography
                                        key={aIndex}
                                        level="body-sm"
                                        sx={{
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: "sm",
                                          color: textTokens.color.primary,
                                          bgcolor: colorTokens.status.success,
                                        }}
                                      >
                                        {ans}
                                      </DarkTypography>
                                    ))}
                                  </Box>
                                </>
                              )}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </TabPanel>
              ))}
            </Tabs>
          </Box>
        </DarkCard>
      </Box>
    </Modal>
  );
}
